import { CSSObject } from '@emotion/react';
import { BreakpointKey, HoneycombTheme } from '../types';

// type StylePropValue = string | number | boolean;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type StylePropValue = any;
type StyleResponsiveValue = { [key in BreakpointKey]?: StylePropValue };
type StyleValue = StylePropValue | StyleResponsiveValue | undefined;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type StyleGetter = (value: any) => CSSObject;
type StyleValueGetterPair = { value: StyleValue | { [x: string]: StyleValue }; getStyles: StyleGetter };

function getClosestBreakpointValue(
    target: BreakpointKey,
    values: { [key in BreakpointKey]?: StylePropValue },
    theme: HoneycombTheme
): StylePropValue | undefined {
    const match = values[target];
    if (match !== undefined) {
        // Exact match
        return match;
    }

    // Get the theme breakpoint keys in reverse order
    const { keys } = theme.breakpoints;
    const reversedKeys = [...keys].reverse();

    // Use this to sort our available keys
    const availableKeys = (Object.keys(values) as BreakpointKey[]).sort(
        (a, b) => reversedKeys.indexOf(a) - reversedKeys.indexOf(b)
    );

    // Get keys which precede our target key
    const preceedingKeys = reversedKeys.slice(reversedKeys.findIndex((key) => key === target) + 1);

    // Take the first available key which exists in our preceeding keys
    const matchingKey = availableKeys.find((key) => preceedingKeys.includes(key));

    return matchingKey ? values[matchingKey] : undefined;
}

export function mergeResponsiveStyleValues(
    values: Record<string, StyleValue>,
    theme: HoneycombTheme
): { [key in BreakpointKey]?: { [x: string]: StylePropValue | undefined } } {
    // Determine the distinct breakpoint keys which we have values for
    const allkeys = Object.keys(values).reduce<BreakpointKey[]>((acc, valueKey) => {
        const value = values[valueKey as BreakpointKey];
        const valueKeys = typeof value === 'object' ? Object.keys(value) : ['xs'];
        return [...acc, ...(valueKeys as BreakpointKey[])];
    }, []);

    // Dedupe keys and sort
    const distinctKeys = theme.fn.breakpoints.getSortedKeys([...new Set(allkeys)]);

    return distinctKeys.reduce<{ [key in BreakpointKey]?: { [x: string]: StylePropValue | undefined } }>(
        (acc, breakpoint) => {
            // Add a key for this breakpoint
            acc[breakpoint] = {};

            Object.keys(values).forEach((valueKey) => {
                const value = values[valueKey];
                const breakpointValue =
                    typeof value === 'object' ? getClosestBreakpointValue(breakpoint, value, theme) : value;

                acc[breakpoint] = { ...acc[breakpoint], ...{ [valueKey]: breakpointValue } };
            });
            return acc;
        },
        {}
    );
}

function getResponsiveStyles(valueGetterPair: StyleValueGetterPair, theme: HoneycombTheme): CSSObject {
    const { value, getStyles } = valueGetterPair;

    if (typeof value === 'object') {
        return theme.fn.breakpoints
            .getSortedKeys(Object.keys(value) as BreakpointKey[])
            .reduce<CSSObject>((acc, breakpoint) => {
                const breakpointValue = value[breakpoint];
                if (breakpointValue !== undefined) {
                    const styles = getStyles(breakpointValue);

                    if (breakpoint === 'xs') {
                        // This is our base value, we don't need a media query.
                        return { ...acc, ...styles };
                    }
                    // Wrap the properties in a media query
                    return { ...acc, [theme.fn.breakpoints.up(breakpoint)]: styles };
                }

                return acc;
            }, {});
    }
    return getStyles(value);
}

export function createResponsiveStyles(values: Array<StyleValueGetterPair>, theme: HoneycombTheme): CSSObject {
    const styles = values.map((value) => {
        return getResponsiveStyles(value, theme);
    });

    // Compose the styles array into a single object
    return styles.reduce<CSSObject>((acc, stylesPartial) => {
        Object.keys(stylesPartial).forEach((property) => {
            let value = stylesPartial[property];
            if (typeof value === 'object' && property in acc) {
                // Append this rule to our existing media query property.
                value = { ...(acc[property] as object), ...(value as object) };
            }
            acc[property] = value;
        });
        return acc;
    }, {});
}
